/**
 * Remember the host's UI language in profiles.language.
 *
 * Server-side mails (plan ended, payment failed, deletion…) are written in
 * profiles.language, which nothing ever updated — so every host got Croatian.
 * The column is the host's own (protect_profile_privileged_columns leaves it
 * writable; the profiles_update policy is id = get_clerk_user_id()).
 * Mails exist in Croatian and English only, and every other dashboard language
 * is shown in English (no other UI translation yet) — so the mail language is
 * what the host actually sees: Croatian UI → 'hr', anything else → 'en'.
 */
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getSupabase } from '@/lib/supabase';
import { useAuthReady } from '@/hooks/useAuthReady';

/** The mail language for a dashboard language: hr → hr, everything else → en. */
export function mailLanguageFor(uiLang: string | null | undefined): 'hr' | 'en' {
  return (uiLang || '').toLowerCase().startsWith('hr') ? 'hr' : 'en';
}
const SYNCED_KEY = 'bookiapp:profile-language-synced';

/** Resolves true when the write succeeded. */
function writeLanguage(userId: string, code: string): Promise<boolean> {
  return Promise.resolve(
    getSupabase()
      .from('profiles')
      .update({ language: code, updated_at: new Date().toISOString() })
      .eq('id', userId),
  ).then(({ error }) => {
    if (error) console.warn('Could not save the language to the profile:', error);
    return !error;
  });
}

/** Callback for an explicit language change (dashboard language picker). */
export function usePersistProfileLanguage() {
  const { userId, isReady } = useAuthReady();

  return useCallback(
    (code: string) => {
      if (!isReady || !userId || !code) return;
      void writeLanguage(userId, mailLanguageFor(code));
    },
    [isReady, userId],
  );
}

/**
 * Once per browser session: if the dashboard language's mail language differs
 * from profiles.language, write it (hosts who never touch the picker still
 * get mails in the language they use).
 */
export function useSyncProfileLanguageOnce() {
  const { userId, isReady } = useAuthReady();
  const { i18n } = useTranslation();
  const uiLang = i18n.language ? mailLanguageFor(i18n.language) : '';

  useEffect(() => {
    if (!isReady || !userId || !uiLang) return;
    const flag = `${SYNCED_KEY}:${userId}`;
    try {
      if (sessionStorage.getItem(flag)) return;
    } catch {
      // storage blocked — still sync, at worst once per page load
    }
    const markDone = () => {
      try {
        sessionStorage.setItem(flag, '1');
      } catch {
        // ignore
      }
    };
    let cancelled = false;
    void (async () => {
      const { data, error } = await getSupabase().from('profiles').select('language').eq('id', userId).maybeSingle();
      // No row yet (profile still being created) or an error → try again later.
      if (cancelled || error || !data) return;
      const stored = ((data as { language?: string | null }).language ?? '').toLowerCase();
      if (stored === uiLang) {
        markDone();
        return;
      }
      // The flag is set only once the write really went through.
      if (await writeLanguage(userId, uiLang)) markDone();
    })();
    return () => {
      cancelled = true;
    };
  }, [isReady, userId, uiLang]);
}
